import { useEffect, useRef, useState } from "react";
import { Howl } from "howler";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Pause, Play, SkipForward, SkipBack, X, Volume2, VolumeX } from "lucide-react";
import { PLAYABLE_TRACKS, MUSIC_TRACKS } from "@/content/music";
import { cn } from "@/lib/cn";
import { useMusicStore } from "./store";

const FADE_MS = 1200;

/**
 * Corner widget. Collapsed = small button. Expanded = card with now-playing,
 * controls, history blurb, and a track list.
 *
 * Howler is used with html5:true so audio streams from the network instead of
 * preloading entire blobs into memory. A single Howl instance per track is
 * cached for crossfading.
 */
export function MusicShelf() {
  const { status, trackIndex, volume, expanded, setExpanded, setVolume, next, prev, jumpTo, enable, disable } =
    useMusicStore();
  const howlRef = useRef<Howl | null>(null);
  const [howlReady, setHowlReady] = useState(false);

  const noPlayable = PLAYABLE_TRACKS.length === 0;
  const safeIndex = noPlayable ? 0 : trackIndex % PLAYABLE_TRACKS.length;
  const track = PLAYABLE_TRACKS[safeIndex];

  // Manage the active Howl when track or status changes
  useEffect(() => {
    if (status !== "playing" || !track) {
      // Fade out and stop any current howl
      if (howlRef.current) {
        const h = howlRef.current;
        h.fade(h.volume(), 0, FADE_MS);
        const id = setTimeout(() => h.stop(), FADE_MS);
        return () => clearTimeout(id);
      }
      return;
    }

    setHowlReady(false);
    const audioUrl = `/api/m/${encodeURIComponent(track.id)}.mp3`;
    const next = new Howl({
      src: [audioUrl],
      format: ["mp3"],
      html5: true,
      volume: 0,
      onload: () => setHowlReady(true),
      onloaderror: () => setHowlReady(false),
      onplayerror: () => {
        // Browser blocked autoplay. Wait for the first user gesture and
        // retry — most kids click "Begin the Garden" within seconds, so
        // the music drifts in just as they start playing.
        const retry = () => {
          next.play();
          next.fade(0, volume, FADE_MS);
          window.removeEventListener("pointerdown", retry);
          window.removeEventListener("keydown", retry);
        };
        window.addEventListener("pointerdown", retry, { once: true });
        window.addEventListener("keydown", retry, { once: true });
      },
      onend: () => useMusicStore.getState().next(),
    });

    const prev = howlRef.current;
    howlRef.current = next;
    next.play();
    next.fade(0, volume, FADE_MS);

    if (prev) {
      prev.fade(prev.volume(), 0, FADE_MS);
      setTimeout(() => {
        prev.stop();
        prev.unload();
      }, FADE_MS + 100);
    }

    return () => {
      // On track change, the next effect run handles cross-fading the new one in.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, track?.id]);

  // Keep volume synced
  useEffect(() => {
    if (howlRef.current && status === "playing") {
      howlRef.current.volume(volume);
    }
  }, [volume, status]);

  // Stop on unmount
  useEffect(() => {
    return () => {
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.unload();
      }
    };
  }, []);

  return (
    <>
      {/* Collapsed toggle button (always visible). Shows the current
          track title when music is playing, so families can see what's
          playing without opening the shelf. */}
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        onClick={() => setExpanded(true)}
        className={cn(
          "fixed bottom-4 right-4 z-40 inline-flex max-w-[min(22rem,calc(100vw-2rem))] items-center gap-2 rounded-full border-2 border-ink/30 bg-parchment-50/95 px-4 py-2 shadow-soft backdrop-blur",
          "hover:scale-[1.02] active:scale-[0.98] transition-transform",
          expanded && "pointer-events-none opacity-0",
        )}
        aria-label={
          status === "playing" && track
            ? `Music shelf — now playing: ${track.title} by ${track.composer}`
            : "Open music shelf"
        }
      >
        <Music className={cn("h-4 w-4 flex-none text-navy", status === "playing" && "animate-shimmer")} />
        {status === "playing" && track && (
          <span className="min-w-0 truncate text-xs font-medium text-ink-light">
            <span className="text-ink-muted">♫ </span>
            {track.title}
          </span>
        )}
      </motion.button>

      {/* Expanded panel */}
      <AnimatePresence>
        {expanded && (
          <motion.aside
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="ink-border wash fixed bottom-4 right-4 z-50 w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-2xl"
            role="dialog"
            aria-label="Music shelf"
          >
            <div className="flex items-center justify-between border-b border-ink/15 px-4 py-2">
              <div className="flex items-center gap-2 text-sm font-medium text-ink-light">
                <Music className="h-4 w-4 text-navy" />
                Music shelf
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="rounded-full p-1 text-ink-muted hover:bg-parchment-100 hover:text-ink"
                aria-label="Close music shelf"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {noPlayable ? (
              <div className="px-4 py-6 text-sm text-ink-muted">
                <p className="mb-2 font-medium text-ink-light">No tracks ready yet.</p>
                <p>
                  Public-domain audio URLs are still being verified for the
                  composer rotation. Check back soon.
                </p>
              </div>
            ) : (
              track && (
                <>
                  {/* Now playing card */}
                  <div className="space-y-3 px-4 py-4">
                    <div className="space-y-1">
                      <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink-muted">
                        {track.era} · {track.composedYear}
                      </div>
                      <div className="font-display text-xl font-semibold leading-tight text-navy">
                        {track.title}
                      </div>
                      <div className="text-sm italic text-ink-light">
                        {track.composer}{" "}
                        <span className="text-ink-muted">({track.composerYears})</span>
                      </div>
                    </div>

                    <p className="text-sm leading-relaxed text-ink-light">{track.history}</p>

                    {track.attribution && (
                      <p className="text-[11px] italic text-ink-muted">{track.attribution}</p>
                    )}

                    {/* Transport controls */}
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={prev}
                          aria-label="Previous track"
                          className="rounded-full p-2 text-navy hover:bg-parchment-100"
                        >
                          <SkipBack className="h-5 w-5" />
                        </button>
                        <button
                          onClick={status === "playing" ? disable : enable}
                          aria-label={status === "playing" ? "Pause" : "Play"}
                          className="rounded-full bg-navy p-3 text-parchment-50 shadow-soft hover:bg-navy-light"
                        >
                          {status === "playing" ? (
                            <Pause className="h-5 w-5" />
                          ) : (
                            <Play className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={next}
                          aria-label="Next track"
                          className="rounded-full p-2 text-navy hover:bg-parchment-100"
                        >
                          <SkipForward className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="flex flex-1 items-center gap-2">
                        <button
                          onClick={() => setVolume(volume > 0 ? 0 : 0.4)}
                          aria-label={volume > 0 ? "Mute" : "Unmute"}
                          className="rounded-full p-1 text-ink-muted hover:text-ink"
                        >
                          {volume > 0 ? (
                            <Volume2 className="h-4 w-4" />
                          ) : (
                            <VolumeX className="h-4 w-4" />
                          )}
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={volume}
                          onChange={(e) => setVolume(Number(e.target.value))}
                          aria-label="Volume"
                          className="h-1 w-full cursor-pointer accent-gold"
                        />
                      </div>
                    </div>

                    {!howlReady && status === "playing" && (
                      <div className="text-xs italic text-ink-muted">Loading…</div>
                    )}
                  </div>

                  {/* Track list */}
                  <div className="border-t border-ink/15">
                    <div className="px-4 pb-3 pt-3 text-xs font-medium uppercase tracking-wider text-ink-muted">
                      Composers
                    </div>
                    <ul className="max-h-64 overflow-y-auto pb-2">
                      {MUSIC_TRACKS.map((t) => {
                        const playable = t.audioUrl !== null;
                        const isCurrent = playable && t.id === track.id;
                        const playableIndex = PLAYABLE_TRACKS.findIndex((p) => p.id === t.id);
                        return (
                          <li key={t.id}>
                            <button
                              disabled={!playable}
                              onClick={() => playable && jumpTo(playableIndex)}
                              className={cn(
                                "flex w-full items-baseline gap-3 px-4 py-1.5 text-left text-sm transition-colors",
                                isCurrent
                                  ? "bg-gold-light/40 text-navy"
                                  : playable
                                    ? "text-ink-light hover:bg-parchment-100"
                                    : "text-ink-muted/60",
                              )}
                            >
                              <span className="min-w-0 flex-1 truncate">
                                <span className="font-medium">{t.composer.split(" ").slice(-1)[0]}</span>
                                <span className="text-ink-muted"> · {t.title}</span>
                              </span>
                              <span className="flex-none text-xs text-ink-muted">
                                {playable ? t.composedYear : "soon"}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              )
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
